import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';

// Node visual variants, mapped 1:1 to the prototype's center-spine timeline
// (#plate-history .tl) node styling: now / vehicle assignment / sale / origin.
export type TimelineNodeKind =
  | 'now'
  | 'vehicle'
  | 'seen'
  | 'sale'
  | 'retention'
  | 'advert'
  | 'origin';

// A historic advertisement of this plate (archive clipping). The card names the
// VENDOR and the asking price only; the publication appears solely in the
// image credit. box is this plate's own line in the clipping, as fractions of
// the square image [x0, y0, x1, y1], or null when its position is unverified
// (the clipping is then shown without a highlight, never with a guessed one).
export interface ArchiveAdvert {
  date: string;          // ISO publication date
  dateLabel: string;     // "30 Sep 1995"
  price: number;
  priceText: string;     // "£2,400"
  priceBasis: 'listed' | 'section';
  priceNote?: string | null; // e.g. "Listed in a section offered at £360 each"
  vendor: string | null;  // null when the advert names no advertiser
  vat: boolean;          // advert states prices are plus VAT
  plateAsPrinted: string;
  image: string;
  box: [number, number, number, number] | null;
  // Square zoom window (fractions of the clipping) that shows the plate at a
  // legible size; null when the line has no verified position.
  view?: [number, number, number, number] | null;
  // How clearly the plate's line prints (line height x edge sharpness); used to
  // pick which of several repeat adverts to show. 0 when unboxed.
  clarity?: number;
  credit: string;
}

// A credited photograph of the plate on a vehicle (Plate Image Researcher).
// Shown with a "Photo: <source>" credit line and a link to the origin; never
// hotlinked (we host our own copy). licence is set for CC/owned media.
export interface VehiclePhoto {
  // Normally a CDN https string; for a member's just-submitted pending photo it
  // is a sanitizer-trusted local object URL (SafeUrl).
  url: string | SafeUrl;
  credit: string;      // e.g. "Jon Lavis · CC BY 2.0", "@spotter · Instagram"
  sourceUrl: string;
  licence?: string;
  cropped?: boolean;
  // A member's own just-submitted photo, awaiting editor approval. Shown ONLY
  // to the contributor, badged "Pending review", so their upload is visible to
  // them immediately without being public. Cleared once approved.
  pending?: boolean;
}

// A single row in the Plate History timeline. Every field is populated by the
// host component from EXISTING PlateX data only (reg_history vehicle
// assignments, solddata sale/auction records, and the plate-detail issue
// fields). Nothing here invents assignments, prices, sources or dates.
export interface TimelineEvent {
  node: TimelineNodeKind;
  // Small mono year tag shown opposite the card ("NOW" for the live row).
  yearTag: string;
  yearTagNow?: boolean;
  // Extra top spacing before grouped events (sales / origin), per prototype.
  gapTop?: boolean;
  // Current vehicle / live row highlight.
  isCurrent?: boolean;

  // ---- vehicle assignment ----
  vehicleName?: string;
  // "Assigned <Month Year>" line for the CURRENT vehicle, derived by the host
  // from the DVLA V5C issue date (a new V5C is issued when a plate is put on a
  // vehicle). Month precision only — a V5C can also be reissued for keeper or
  // address changes, so a day-precise claim would overstate the evidence.
  vehicleAssigned?: string | null;
  // "Registered <Month Year>" — the VEHICLE's own DVLA first-registration date.
  // A vehicle fact, deliberately separated from the plate facts (assigned /
  // observed): a 2016-registered car may only have carried the plate since 2026.
  vehicleRegistered?: string | null;
  vehicleColour?: string | null;
  // Hex for the card's colour accent bar (--card-accent), resolved from the
  // colour name by the host. Null when the colour is unknown/unmapped (the
  // card then falls back to the neutral default bar).
  vehicleColourHex?: string | null;
  // Plate-on-vehicle tenure from OBSERVATION evidence only (MOT sightings /
  // present status) — e.g. "Observed 2011 – 2022", "Present". Never derived
  // from the vehicle's registration date.
  vehiclePeriod?: string;
  // Brand logo asset path (assets/car-logos/<slug>.svg) resolved from the make,
  // or null when the make has no stored logo (the generic car glyph is shown).
  vehicleLogo?: string | null;
  // Conflicting or uncertain records about this card, stated rather than
  // silently resolved (a sighting before the plate's release, a DVLA record
  // naming a different car).
  vehicleNote?: string | null;

  // ---- photo (Plate Image Researcher) ----
  // A credited photo of the plate on this vehicle. On a 'vehicle' node it is a
  // matched image; a 'seen' node is a photo-sourced record where reg_history has
  // no matching vehicle (the photo IS the evidence). Marked by the "Photo:"
  // credit line, never a badge.
  vehiclePhoto?: VehiclePhoto;

  // ---- now (on the market) ----
  chip?: string;
  nowText?: string;
  nowSub?: string;

  // ---- card eyebrow (now / sale / origin) ----
  kind?: string;

  // ---- sale / auction ----
  saleTitle?: string;
  saleAmount?: string | null;
  saleMeta?: string;

  // ---- archive advert ----
  advert?: ArchiveAdvert;

  // ---- origin / series ----
  originLetter?: string;
  originTitle?: string;
  originSub?: string;
  // Why the mark sat unissued, when we know: "Never issued. Held in DVLA stock
  // until March 2025."
  originNote?: string | null;
  // Rendered faded and flat. The origin node is an INFERENCE from the mark's
  // characters — the series a suffix letter denotes, the office an area code
  // maps to — not a record that anything happened to this plate. Styling it like
  // the sale and vehicle events gave it the same evidential weight, which is how
  // "First issued August 1981" came to sit above a genuine 2025 DVLA sale.
  inert?: boolean;
}

// "Plate History" timeline (resale / provenance) for plate-detail-v2. Pure
// presentational component: it renders the TimelineEvent[] built by the host
// from live PlateX data, using the center-spine visual language ported from the
// "PlateX 34" resale-timeline prototype. The plate mark in the live row is set
// in the authentic plate font (UKReg) as text, never recreated here.
@Component({
  selector: 'pla-pdv2-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
})
export class TimelineComponent {
  @Input() public events: TimelineEvent[] = [];
  // Show a quiet "+ Add a photo" link on vehicle cards that have no photo; the
  // host opens its add-photo form (projected below the spine) for that car.
  @Input() public photoCta = false;
  @Output() public addPhoto = new EventEmitter<string>();

  // Substantive history = at least one real vehicle assignment or sale. The
  // section is otherwise rendered as a single quiet "no history" card rather
  // than an empty/skeletal timeline.
  public get hasHistory(): boolean {
    return (this.events || []).some(
      (e) =>
        e.node === 'vehicle' ||
        e.node === 'seen' ||
        e.node === 'sale' ||
        e.node === 'advert'
    );
  }

  // Archive advert lightbox: the clipping, with this plate's line outlined.
  public openAdvert: ArchiveAdvert | null = null;

  // Open zoomed to the plate when its position is known; the reader can switch
  // to the whole clipping.
  public adZoomed = true;

  // The box waits for the clipping, so it never shows over a blank frame.
  public adLoaded = false;

  public showAdvert(ad: ArchiveAdvert): void {
    this.openAdvert = ad;
    this.adZoomed = true;
    this.adLoaded = false;
  }

  public toggleAdZoom(): void {
    this.adZoomed = !this.adZoomed;
  }

  // Width of the square frame in px, read when the clipping loads.
  public adFramePx = 0;

  // A light zoom: the plate's line shows at about the size of the caption text,
  // so the advert keeps its look and more of it stays in view.
  private static readonly AD_LINE_PX = 12;

  // Window width (fraction of the clipping) for the light zoom; 1 when the
  // whole advert already shows the plate at that size.
  private adZoomWidth(ad: ArchiveAdvert): number {
    if (!ad.view || !ad.box) return 1;
    const [bx0, by0, bx1, by1] = ad.box;
    const frame = this.adFramePx || 400;
    const w = Math.max(((by1 - by0) * frame) / TimelineComponent.AD_LINE_PX,
                       ad.view[2] - ad.view[0], (bx1 - bx0) * 1.5);
    return w > 0.9 ? 1 : w;
  }

  public adCanZoom(ad: ArchiveAdvert): boolean {
    return this.adZoomWidth(ad) < 1;
  }

  private adView(ad: ArchiveAdvert): [number, number, number, number] {
    const w = this.adZoomed ? this.adZoomWidth(ad) : 1;
    if (w >= 1) return [0, 0, 1, 1];
    const [bx0, by0, bx1, by1] = ad.box;
    const cx = (bx0 + bx1) / 2, cy = (by0 + by1) / 2;
    const x0 = Math.min(Math.max(cx - w / 2, 0), 1 - w), y0 = Math.min(Math.max(cy - w / 2, 0), 1 - w);
    return [x0, y0, x0 + w, y0 + w];
  }

  // The clipping scaled and shifted so the zoom window fills the square frame.
  public imgStyle(ad: ArchiveAdvert): { [k: string]: string } {
    const [vx0, vy0, vx1] = this.adView(ad);
    const w = vx1 - vx0;
    return {
      width: 100 / w + '%',
      height: 100 / w + '%',
      left: (-vx0 / w) * 100 + '%',
      top: (-vy0 / w) * 100 + '%',
    };
  }

  public closeAdvert(): void {
    this.openAdvert = null;
  }

  @HostListener('document:keydown.escape')
  public onEscape(): void {
    this.closeAdvert();
  }

  // Highlight geometry as % of the square clipping, so it scales with the image.
  // The box is grown past the OCR line so it never clips the plate's own
  // characters; overlapping the neighbouring lines a little is fine.
  public boxStyle(ad: ArchiveAdvert): { [k: string]: string } | null {
    if (!ad.box) return null;
    // Box in the coordinates of the window being shown (whole clipping or zoom).
    const [vx0, vy0, vx1] = this.adView(ad);
    const vw = vx1 - vx0;
    const [bx0, by0, bx1, by1] = ad.box.map((v, i) => (v - (i % 2 ? vy0 : vx0)) / vw);
    const h = by1 - by0;
    const padY = Math.min(h * 0.35, 0.015);
    const padX = Math.min(Math.max(h * 0.4, (bx1 - bx0) * 0.04), 0.02);
    const x0 = Math.max(0, bx0 - padX);
    const y0 = Math.max(0, by0 - padY);
    const x1 = Math.min(1, bx1 + padX);
    const y1 = Math.min(1, by1 + padY);
    return {
      left: x0 * 100 + '%',
      top: y0 * 100 + '%',
      width: (x1 - x0) * 100 + '%',
      height: (y1 - y0) * 100 + '%',
    };
  }

  // Stable identity per row so Angular reuses the DOM (and the vehicle <img>)
  // across change-detection passes instead of tearing it down — which is what
  // made the photo flicker on scroll.
  public trackRow(_i: number, ev: TimelineEvent): string {
    const photoKey = ev.vehiclePhoto
      ? typeof ev.vehiclePhoto.url === 'string'
        ? ev.vehiclePhoto.url
        : 'local'
      : '';
    const advertKey = ev.advert ? ev.advert.image + ev.advert.date : '';
    return [ev.node, ev.yearTag, ev.vehicleName || '', photoKey, advertKey].join('|');
  }
}
